import { useCallback, useMemo } from 'react'

import { useEntitiesColors } from 'contexts/EntitiesColorsContext'
import type { TGraphQLProviders } from 'entities/provider'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { resolvePromotedNicheTargetName } from 'modules/niche/common/promoted-niche.utils'

import { EMPTY_METRICS, GET_TERRITORY_NICHES_BRANDED_VS_UGC_LOG } from './use-niches-branded-vs-ugc.consts'
import type { TBrandedVsUgcNiche, TUseNichesBrandedVsUgcParams } from './use-niches-branded-vs-ugc.types'
import { getDateVariables, sortNichesByMetric, valueByMetric } from './use-niches-branded-vs-ugc.utils'

export const useNichesBrandedVsUgc = ({
  territoryId,
  contextId,
  selfContentBrandIds,
  paidBrandIds,
  earnedBrandIds,
  territoryIds,
  providers,
  uploadDate,
  sortMetric = 'engagement',
  sortSide = 'branded',
  skip,
}: TUseNichesBrandedVsUgcParams) => {
  const { getColorByEntityId } = useEntitiesColors()

  const dateVariables = useMemo(() => getDateVariables(uploadDate), [uploadDate])

  const { data, loading, error, refetch, networkStatus } = useTerritoryQueries({
    queryName: 'getTerritoryNichesBrandedVsUgc',
    errorLog: GET_TERRITORY_NICHES_BRANDED_VS_UGC_LOG,
    queryVariables: {
      territoryId,
      contextId,
      selfContentBrandIds,
      paidBrandIds,
      earnedBrandIds,
      // Promoted-Niche scope filter — restricts metrics to the promoted universe.
      territoryIds,
      providers: providers as TGraphQLProviders,
      ...dateVariables,
    },
    options: {
      skip: skip || !territoryId || !contextId,
    },
  })

  const niches = useMemo<TBrandedVsUgcNiche[]>(() => {
    const territory = data?.getTerritory
    if (!territory || !('territoryNiches' in territory)) return []

    const rawNiches = territory.territoryNiches?.territoryNiches?.data ?? []

    const mapped = rawNiches.map(niche => {
      const branded = niche.territoryPerformance?.brandedMetrics
      const brandedSampled = niche.territoryPerformance?.brandedSampledMetrics
      const ugc = niche.territoryPerformance?.ugcMetrics
      const ugcSampled = niche.territoryPerformance?.ugcSampledMetrics
      return {
        territoryId: niche.territoryId,
        // A promoted niche keeps its own `name` here, so read the promoted
        // Category name (which carries the country code) from its promotion —
        // same resolution the niches tab applies via `useNicheWithContext`.
        name: resolvePromotedNicheTargetName(niche) || niche.name,
        color: getColorByEntityId(niche.territoryId, 'topics'),
        brandedMetrics: branded
          ? {
              totalPosts: branded.totalPosts,
              totalEngagement: branded.totalEngagement,
              totalViews: branded.totalViews,
              avgEngagement: brandedSampled?.avgEngagement ?? 0,
              avgViews: brandedSampled?.avgViews ?? 0,
            }
          : EMPTY_METRICS,
        ugcMetrics: ugc
          ? {
              totalPosts: ugc.totalPosts,
              totalEngagement: ugc.totalEngagement,
              totalViews: ugc.totalViews,
              avgEngagement: ugcSampled?.avgEngagement ?? 0,
              avgViews: ugcSampled?.avgViews ?? 0,
            }
          : EMPTY_METRICS,
      }
    })

    return sortNichesByMetric(mapped, sortMetric, sortSide)
  }, [data, getColorByEntityId, sortMetric, sortSide])

  const getValueByMetric = useCallback(valueByMetric, [])

  return {
    niches,
    loading,
    error,
    refetch,
    networkStatus,
    valueByMetric: getValueByMetric,
  }
}
